import React from "react";
import { Box, Paper, Chip, alpha, useTheme, Typography } from "@mui/material";
import {
  HourglassEmpty,
  RestaurantMenu,
  CheckCircle,
  Done,
  ViewList,
  TaskAlt,
  Cancel,
} from "@mui/icons-material";

const OrderStatusNavigation = ({ orderTab, setOrderTab, statusCounts = {} }) => {
  const theme = useTheme();

  const statusButtons = [
    {
      key: "all",
      label: "All Orders",
      icon: <ViewList sx={{ fontSize: 16 }} />,
      color: { main: "#667eea", dark: "#764ba2" },
    },
    {
      key: "pending",
      label: "Pending",
      icon: <HourglassEmpty sx={{ fontSize: 16 }} />,
      color: { main: "#f59e0b", dark: "#d97706" },
    },
    {
      key: "preparing",
      label: "Preparing",
      icon: <RestaurantMenu sx={{ fontSize: 16 }} />,
      color: { main: "#667eea", dark: "#764ba2" },
    },
    {
      key: "ready",
      label: "Ready",
      icon: <CheckCircle sx={{ fontSize: 16 }} />,
      color: { main: "#10b981", dark: "#059669" },
    },
    {
      key: "served",
      label: "Served",
      icon: <Done sx={{ fontSize: 16 }} />,
      color: { main: "#6b7280", dark: "#4b5563" },
    },
    {
      key: "completed",
      label: "Completed",
      icon: <TaskAlt sx={{ fontSize: 16 }} />,
      color: { main: "#059669", dark: "#047857" },
    },
    {
      key: "cancelled",
      label: "Cancelled",
      icon: <Cancel sx={{ fontSize: 16 }} />,
      color: { main: "#ef4444", dark: "#dc2626" },
    },
  ];

  const allCount = Object.values(statusCounts).reduce((sum, v) => sum + (v || 0), 0);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        gap: 1.5,
        flexWrap: "wrap",
      }}
    >
      {statusButtons.map((status) => {
        const isActive = orderTab === status.key;
        const count = status.key === "all" ? allCount : (statusCounts[status.key] || 0);

        return (
          <Paper
            key={status.key}
            elevation={0}
            onClick={() => setOrderTab(status.key)}
            sx={{
              cursor: "pointer",
              px: 2.5,
              py: 1.5,
              borderRadius: "14px",
              bgcolor: "white",
              border: "1px solid",
              borderColor: isActive
                ? alpha(status.color.main, 0.3)
                : alpha(theme.palette.divider, 0.1),
              boxShadow: isActive
                ? `0 8px 16px ${alpha(status.color.main, 0.2)}`
                : `0 2px 4px ${alpha("#000", 0.04)}`,
              position: "relative",
              overflow: "hidden",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: `0 12px 20px ${alpha(status.color.main, 0.25)}`,
                borderColor: alpha(status.color.main, 0.4),
              },
            }}
          >
            {/* Background Decoration */}
            <Box
              sx={{
                position: "absolute",
                top: -15,
                right: -15,
                width: 60,
                height: 60,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${alpha(status.color.main, isActive ? 0.15 : 0.08)}, ${alpha(status.color.dark, isActive ? 0.1 : 0.04)})`,
              }}
            />

            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              {/* Icon Box */}
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: isActive
                    ? `linear-gradient(135deg, ${status.color.main}, ${status.color.dark})`
                    : alpha(status.color.main, 0.12),
                  boxShadow: isActive
                    ? `0 4px 12px ${alpha(status.color.main, 0.25)}`
                    : "none",
                  transition: "all 0.3s ease",
                }}
              >
                {React.cloneElement(status.icon, {
                  sx: {
                    fontSize: 18,
                    color: isActive ? "#fff" : status.color.main,
                  },
                })}
              </Box>

              {/* Label and Count */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    color: isActive
                      ? theme.palette.text.primary
                      : alpha(theme.palette.text.primary, 0.7),
                    letterSpacing: "-0.2px",
                  }}
                >
                  {status.label}
                </Typography>
                <Chip
                  label={`${count} ${count === 1 ? "order" : "orders"}`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    bgcolor: isActive
                      ? alpha(status.color.main, 0.15)
                      : alpha(theme.palette.grey[500], 0.08),
                    color: isActive
                      ? status.color.main
                      : theme.palette.text.secondary,
                    border: `1px solid ${isActive ? alpha(status.color.main, 0.2) : "transparent"}`,
                    "& .MuiChip-label": {
                      px: 1,
                    },
                  }}
                />
              </Box>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
};

export default OrderStatusNavigation;
